/**
 * Eine Klausur mit allem direkt sichtbar (Leitlinie 2): Termin und Countdown,
 * Themenliste und Lernplan - beides abhakbar (F-22 Stufe 3).
 */
import { useState } from 'react';
import { CalendarPlus, Plus, Trash2, X } from 'lucide-react';

import { Input } from '@/ui/primitives/Input';
import { Button } from '@/ui/primitives/Button';
import { AreaDot } from '@/modules/areas/AreaDot';
import { useLinkNames } from '@/modules/links/linkNames';
import {
  addLernEintrag, addThema, deleteExam, lerntageAnlegen, removeThema, toggleThema,
} from '@/db/repositories/exams';
import { deleteTask, toggleTaskDone } from '@/db/repositories/tasks';
import { countdown, klausurDatum } from '@/domain/school/exams';
import { addDays, differenceInDays, formatDe, today } from '@/lib/datetime';
import { cn } from '@/lib/utils/cn';
import type { CalendarEvent, Task } from '@/domain/types';

export function KlausurKarte({ klausur, lernplan }: { klausur: CalendarEvent; lernplan: Task[] }) {
  const { subjects } = useLinkNames();
  const fach = klausur.subjectId ? subjects.get(klausur.subjectId) : undefined;
  const heute = today();
  const datum = klausurDatum(klausur);
  const rest = differenceInDays(heute, datum);
  const themen = klausur.klausur?.themen ?? [];
  const [thema, setThema] = useState('');
  const vortag = addDays(datum, -1);
  const [lernTag, setLernTag] = useState(heute < datum ? heute : vortag);
  const [lernText, setLernText] = useState('');

  return (
    <li className="flex flex-col gap-3 rounded-lg border border-line bg-surface px-4 py-3">
      <div className="flex flex-wrap items-center gap-2">
        <AreaDot color={fach?.color ?? 'var(--color-line-strong)'} size="sm" />
        <span className="min-w-32 flex-1 truncate text-sm font-medium">{klausur.title}</span>
        <span className={cn(
          'shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium',
          rest <= 3 ? 'bg-warn/15 text-warn' : 'bg-surface-raised text-ink-muted',
        )}>
          {countdown(heute, datum)}
        </span>
        <button
          type="button"
          onClick={() => { if (window.confirm(`„${klausur.title}" samt Lernplan löschen? Kommt für 30 Tage in den Papierkorb.`)) void deleteExam(klausur.id); }}
          aria-label={`${klausur.title} löschen`}
          className="grid size-7 place-items-center rounded-md text-ink-faint hover:bg-surface-raised hover:text-danger"
        >
          <Trash2 className="size-3.5" />
        </button>
      </div>
      <p className="-mt-2 text-[12px] text-ink-muted">
        {formatDe(datum, 'EEEEEE, dd.MM.yyyy')}, {String(klausur.start).slice(11, 16)}–{String(klausur.end).slice(11, 16)}
        {klausur.location ? ` · ${klausur.location}` : ''}{fach ? ` · ${fach.name}` : ''}
      </p>

      <div className="grid gap-3 md:grid-cols-2">
        {/* Themen */}
        <div className="flex flex-col gap-1.5">
          <h4 className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">
            Themen {themen.length > 0 && `(${themen.filter((t) => t.done).length}/${themen.length} sitzen)`}
          </h4>
          <ul className="flex flex-col gap-1">
            {themen.map((t) => (
              <li key={t.id} className="group flex items-center gap-2 text-sm">
                <input type="checkbox" checked={t.done} onChange={() => void toggleThema(klausur.id, t.id)}
                  aria-label={`Thema ${t.text} sitzt`} className="size-4 shrink-0 accent-[var(--color-accent)]" />
                <span className={cn('min-w-0 flex-1 truncate', t.done && 'text-ink-faint line-through')}>{t.text}</span>
                <button type="button" onClick={() => void removeThema(klausur.id, t.id)} aria-label={`Thema ${t.text} entfernen`}
                  className="grid size-5 shrink-0 place-items-center rounded text-ink-faint opacity-0 hover:text-ink focus:opacity-100 group-hover:opacity-100">
                  <X className="size-3" />
                </button>
              </li>
            ))}
          </ul>
          <Input value={thema} onChange={(e) => setThema(e.target.value)} placeholder="Thema hinzufügen (Enter)"
            aria-label={`Thema für ${klausur.title}`} className="h-8 text-[12px]"
            onKeyDown={(e) => { if (e.key === 'Enter') { void addThema(klausur.id, thema); setThema(''); } }} />
        </div>

        {/* Lernplan */}
        <div className="flex flex-col gap-1.5">
          <h4 className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">
            Lernplan {lernplan.length > 0 && `(${lernplan.filter((t) => t.status === 'erledigt').length}/${lernplan.length})`}
          </h4>
          <ul className="flex flex-col gap-1">
            {lernplan.map((t) => (
              <li key={t.id} className="group flex items-center gap-2 text-sm">
                <input type="checkbox" checked={t.status === 'erledigt'} onChange={() => void toggleTaskDone(t.id)}
                  aria-label={`${t.title} am ${t.plannedDate} erledigt`} className="size-4 shrink-0 accent-[var(--color-accent)]" />
                <span className={cn('w-16 shrink-0 tabular-nums text-[12px]', t.plannedDate === heute ? 'font-semibold text-accent-text' : 'text-ink-faint')}>
                  {t.plannedDate ? formatDe(t.plannedDate, 'EEEEEE dd.MM.') : ''}
                </span>
                <span className={cn('min-w-0 flex-1 truncate', t.status === 'erledigt' && 'text-ink-faint line-through')}>{t.title}</span>
                <button type="button" onClick={() => void deleteTask(t.id)} aria-label={`${t.title} aus dem Lernplan entfernen`}
                  className="grid size-5 shrink-0 place-items-center rounded text-ink-faint opacity-0 hover:text-ink focus:opacity-100 group-hover:opacity-100">
                  <X className="size-3" />
                </button>
              </li>
            ))}
          </ul>
          {rest > 0 && (
            <>
              <div className="flex gap-1.5">
                <div className="w-36 shrink-0">
                  <Input type="date" value={lernTag} min={heute} max={vortag} onChange={(e) => setLernTag(e.target.value)}
                    aria-label="Lerntag" className="h-8 text-[12px]" />
                </div>
                <Input value={lernText} onChange={(e) => setLernText(e.target.value)} placeholder="Was lernen?"
                  aria-label={`Lernplan-Eintrag für ${klausur.title}`} className="h-8 text-[12px]"
                  onKeyDown={async (e) => { if (e.key === 'Enter' && await addLernEintrag(klausur.id, lernTag, lernText)) setLernText(''); }} />
                <button type="button" aria-label="Lernplan-Eintrag hinzufügen" disabled={!lernText.trim()}
                  onClick={async () => { if (await addLernEintrag(klausur.id, lernTag, lernText)) setLernText(''); }}
                  className="grid size-8 shrink-0 place-items-center rounded-md border border-line text-ink-faint hover:text-ink disabled:opacity-40">
                  <Plus className="size-3.5" />
                </button>
              </div>
              <Button size="sm" variant="ghost" className="self-start" onClick={() => void lerntageAnlegen(klausur.id)}>
                <CalendarPlus className="size-3.5" /> Täglich bis zum Vortag eintragen
              </Button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}
